import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { parseGpx, type GeofenceShape } from '@raad/geo';
import { PublishTemplateForm } from '@/features/zones/PublishTemplateForm';
import type { TemplateTool } from '@/features/zones/TemplateDrawMap';

import {
  useOutdatedCopies,
  useUpdateTemplate,
  useZoneTemplates,
  type ZoneTemplate,
} from '@/features/zones/useZoneLibrary';

/**
 * Zone library — the staff side of slices 24d and 24e.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT CHANGED IN 24e, AND WHY THE OLD COMMENT HERE WAS WRONG BY THE TIME IT
 * WAS READ.
 * ─────────────────────────────────────────────────────────────────────────────
 * This header used to say drawing "needs a map, and the admin portal has none —
 * MapLibre lands with the global live map (slice 17d)". 17d shipped, MapLibre is
 * in this bundle, and Mahad asked for the drawing map (26 Aug). Both halves of
 * that sentence expired, which is exactly why it is being rewritten rather than
 * left as a comment describing a state that no longer holds.
 *
 * Here now: draw a shape, name and file it, publish it — plus the library as it
 * stands, retire/restore, and the outdated-copies list.
 *
 * 🔴 THE AUTHORING REFUSAL WAS LIFTED, NOT FORGOTTEN. 24d had no
 * geometry-accepting endpoint at all, because an invented boundary is a
 * fabricated shape that mis-fires alerts silently. That reasoning still stands.
 * What changed is that a HUMAN draws here — the only thing that ever built a
 * real library. The code enforces `requireSuperAdmin` and an audit row; it
 * cannot tell a person's mouse from a model's memory, so the rule that no agent
 * may draw a template lives in CLAUDE.md, where every agent is bound by it.
 *
 * STILL NOT HERE: editing an existing template's geometry, and the `version`
 * bump that makes the outdated-copies list live. That is 24g — the version
 * exists to answer "is this copy stale in a way that matters", and proving it
 * deserves its own controls rather than riding along with this.
 */

function TemplateRow({ tpl }: { tpl: ZoneTemplate }) {
  const { t } = useTranslation();
  const update = useUpdateTemplate();

  return (
    <tr className="border-b border-raad-border last:border-b-0">
      <td className="py-2.5 pr-4">
        <span className="font-medium">{tpl.label}</span>
        {tpl.notes !== null ? (
          <span className="mt-0.5 block text-[11.5px] text-raad-muted">{tpl.notes}</span>
        ) : null}
      </td>
      <td className="py-2.5 pr-4">{t(`library.purpose.${tpl.purpose}`)}</td>
      <td className="py-2.5 pr-4">
        {t(`library.kind.${tpl.kind}`)}
        {tpl.kind === 'corridor' && tpl.corridorWidthM !== null ? (
          <span className="text-raad-muted">{` ±${String(tpl.corridorWidthM)} m`}</span>
        ) : null}
      </td>
      <td className="py-2.5 pr-4">{t(`library.alertRule.${tpl.suggestedAlertRule}`)}</td>
      <td className="py-2.5 pr-4 num">{tpl.version}</td>
      <td className="py-2.5">
        <button
          type="button"
          onClick={() => update.mutate({ id: tpl.id, body: { active: !tpl.active } })}
          disabled={update.isPending}
          className="min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold transition-colors hover:bg-raad-primary/10 disabled:opacity-50"
        >
          {/* RETIRE, never delete — a template a tenant already copied is part of
              that copy's provenance, and deleting it would orphan the reference. */}
          {tpl.active ? t('library.retire') : t('library.restore')}
        </button>
      </td>
    </tr>
  );
}

const TOOLS: ReadonlyArray<{ id: TemplateTool; key: string }> = [
  { id: 'circle', key: 'library.publish.toolCircle' },
  { id: 'polygon', key: 'library.publish.toolPolygon' },
  { id: 'corridor', key: 'library.publish.toolCorridor' },
];

/**
 * The 24e authoring panel: pick a tool, draw, name it, publish.
 *
 * The map is LAZY-LOADED for the reason GlobalLive already established —
 * MapLibre plus terra-draw is by far the largest thing in this bundle and every
 * other admin screen is a table. Staff who never open the library never pay for
 * it.
 */
const TemplateDrawMap = lazy(() =>
  import('@/features/zones/TemplateDrawMap').then((m) => ({ default: m.TemplateDrawMap })),
);

// Exported for the container-level test (GPX notice + width gate). The map is
// mocked there — jsdom has no WebGL — so this exercises the PARSING and NOTICE
// this component owns, which is the half web keeps in its container too.
export function DrawAndPublish() {
  const { t } = useTranslation();
  // 🔴 STARTS DISARMED, and returns to disarmed when a shape completes. A map
  // that is always armed turns every stray click into new geometry.
  const [tool, setTool] = useState<TemplateTool | null>(null);
  const [shape, setShape] = useState<GeofenceShape | null>(null);
  const [clearNonce, setClearNonce] = useState(0);

  // 🔴 PARSING LIVES HERE, NOT IN THE MAP — mirroring web's split, where the
  // container parses and owns the notice and the map stays a pure geometry
  // surface. On success we hand the parsed path to the map and bump the nonce;
  // the map lands it on the hand-draw completion path (editable corridor).
  const [importPath, setImportPath] = useState<[number, number][]>();
  const [importNonce, setImportNonce] = useState(0);
  // The outcome is ALWAYS stated: a truncated import is a corridor the customer
  // did not draw, and a corridor decides whether a phone rings — silence here is
  // a wrong default, not a neutral one.
  const [gpxNotice, setGpxNotice] = useState<
    { kind: 'truncated'; points: number } | { kind: 'error'; reason: string } | null
  >(null);

  async function importGpx(file: File): Promise<void> {
    setGpxNotice(null);
    let text: string;
    try {
      text = await file.text();
    } catch {
      setGpxNotice({ kind: 'error', reason: 'not_xml' });
      return;
    }
    const parsed = parseGpx(text);
    if (!parsed.ok) {
      setGpxNotice({ kind: 'error', reason: parsed.reason });
      return;
    }
    setImportPath(parsed.coordinates);
    setImportNonce((n) => n + 1);
    // The tool disarms itself on the completion path; reflect that here too so
    // the toolbar shows nothing armed after an import.
    setTool(null);
    if (parsed.truncated) setGpxNotice({ kind: 'truncated', points: parsed.pointCount });
  }

  return (
    <div className="mt-4 rounded-panel border border-raad-border bg-raad-panel p-4">
      <h2 className="font-display text-sm font-bold">{t('library.publish.title')}</h2>

      <div role="group" aria-label={t('library.publish.title')} className="mt-3 flex flex-wrap gap-1.5">
        {TOOLS.map((x) => (
          <button
            key={x.id}
            type="button"
            onClick={() => setTool(tool === x.id ? null : x.id)}
            aria-pressed={tool === x.id}
            className={[
              'min-h-8 rounded-full border px-3 text-[11.5px] font-semibold transition-colors',
              tool === x.id
                ? 'border-raad-primary bg-raad-primary/10 text-raad-text'
                : 'border-raad-border text-raad-muted hover:bg-raad-primary/5',
            ].join(' ')}
          >
            {t(x.key)}
          </button>
        ))}
        {/*
          🔴 IMPORT CREATES A CORRIDOR — so it sits BESIDE the corridor tool, not
          gated behind it. A styled <label> wraps a hidden file input; the input
          is reset (value = '') after each pick so choosing the SAME file twice in
          a row still fires `change`.
        */}
        <label className="min-h-8 cursor-pointer rounded-full border border-raad-border px-3 text-[11.5px] font-semibold leading-8 text-raad-muted transition-colors hover:bg-raad-primary/5">
          {t('library.import.button')}
          <input
            type="file"
            accept=".gpx,application/gpx+xml"
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void importGpx(file);
            }}
          />
        </label>
      </div>

      {gpxNotice !== null ? (
        <p
          role={gpxNotice.kind === 'error' ? 'alert' : 'status'}
          className={[
            'mt-2 text-[11.5px]',
            gpxNotice.kind === 'error' ? 'text-raad-error' : 'text-raad-muted',
          ].join(' ')}
        >
          {gpxNotice.kind === 'truncated'
            ? t('library.import.truncated', { count: gpxNotice.points })
            : t(`library.import.error.${gpxNotice.reason}`)}
        </p>
      ) : null}

      <div className="mt-3 grid gap-4 lg:grid-cols-[1fr_20rem]">
        <Suspense
          fallback={
            <div className="h-[28rem] w-full rounded-panel border border-raad-border bg-raad-input" />
          }
        >
          <TemplateDrawMap
            tool={tool}
            onDrawn={setShape}
            onDisarm={() => setTool(null)}
            clearNonce={clearNonce}
            importPath={importPath}
            importNonce={importNonce}
          />
        </Suspense>
        <PublishTemplateForm
          shape={shape}
          onPublished={() => {
            // Drop the scratch drawing so a just-published template does not
            // linger on the map as a shape that looks unsaved.
            setShape(null);
            setClearNonce((n) => n + 1);
            setGpxNotice(null);
          }}
        />
      </div>
    </div>
  );
}

export function ZoneLibrary() {
  const { t } = useTranslation();
  const q = useZoneTemplates();
  const outdated = useOutdatedCopies();

  if (q.isPending) return <p className="text-sm text-raad-muted">{t('library.loading')}</p>;
  if (q.isError) {
    return (
      <p role="alert" className="text-sm text-raad-error">
        {t('library.error')}
      </p>
    );
  }

  const templates = Array.isArray(q.data?.templates) ? q.data.templates : null;
  if (templates === null) {
    return (
      <p role="alert" className="text-sm text-raad-error">
        {t('library.error')}
      </p>
    );
  }

  const copies = Array.isArray(outdated.data?.copies) ? outdated.data.copies : [];

  return (
    <section aria-labelledby="library-heading">
      <h1 id="library-heading" className="font-display text-xl font-bold tracking-tight">
        {t('library.title')}
      </h1>
      <p className="mt-1 max-w-2xl text-[12.5px] leading-relaxed text-raad-muted">
        {t('library.explainer')}
      </p>

      <DrawAndPublish />

      {templates.length === 0 ? (
        <p className="mt-4 rounded-panel border border-raad-border bg-raad-panel p-4 text-sm text-raad-muted">
          {t('library.empty')}
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-panel border border-raad-border bg-raad-panel px-4">
          <table className="w-full min-w-[40rem] text-sm">
            <thead>
              <tr className="border-b border-raad-border text-[11px] uppercase tracking-wide text-raad-muted">
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('library.col.name')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('library.col.purpose')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('library.col.shape')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('library.col.suggested')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('library.col.version')}
                </th>
                <th scope="col" className="py-2.5 text-left font-semibold">
                  {t('library.col.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {templates.map((tpl) => (
                <TemplateRow key={tpl.id} tpl={tpl} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <section className="mt-6 rounded-panel border border-raad-border bg-raad-panel p-4 sm:p-5">
        <h2 className="text-[15px] font-semibold">{t('library.outdated.title')}</h2>
        {/* 🔴 THE ARCHITECTURE'S COST, ON SCREEN. Applying a template COPIES it,
            so a later fix to the template does not reach the copies. Without this
            list that divergence would be invisible — which is the strongest
            argument against copy-over-reference, and the reason it is rendered. */}
        <p className="mt-1 text-[11.5px] leading-relaxed text-raad-muted">
          {t('library.outdated.explainer')}
        </p>
        {copies.length === 0 ? (
          <p className="mt-3 text-[12.5px] text-raad-muted">{t('library.outdated.none')}</p>
        ) : (
          <ul className="mt-3 divide-y divide-raad-border">
            {copies.map((c) => (
              <li key={c.geofenceId} className="flex flex-wrap items-center gap-x-3 py-2.5 text-sm">
                <span className="font-medium">{c.tenantName}</span>
                <span>{c.geofenceName}</span>
                <span className="ms-auto text-[12px] text-raad-muted num">
                  v{c.copyVersion} → v{c.templateVersion}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}

export default ZoneLibrary;
